import { useCallback, useEffect, useState } from "react";
import { api, API } from "./api";
import { useAuth } from "../context/AuthContext";

export function useLearning() {
  const { user } = useAuth();
  const [progress, setProgress] = useState({});
  const [certificates, setCertificates] = useState({});
  const [loaded, setLoaded] = useState(false);

  const loadCertificates = useCallback(async () => {
    const { data } = await api.get("/portal/certificates");
    setCertificates(Object.fromEntries(data.map((c) => [c.course_slug, c])));
  }, []);

  const reload = useCallback(async () => {
    if (!user) { setProgress({}); setCertificates({}); setLoaded(user === false); return; }
    const { data } = await api.get("/portal/learning");
    setProgress(Object.fromEntries(data.map((p) => [p.course_slug, p])));
    setLoaded(true);
    await loadCertificates();
  }, [user, loadCertificates]);

  useEffect(() => { reload().catch(() => {}); }, [reload]);

  const toggle = async (slug, lessonId, completed) => {
    const { data } = await api.post(`/portal/learning/${slug}/lessons/${lessonId}`, { completed });
    setProgress((p) => ({ ...p, [slug]: data }));
    if (data.certificate_id && !certificates[slug]) await loadCertificates();
    return data;
  };

  const submitQuiz = async (slug, answers) => {
    const { data } = await api.post(`/portal/learning/${slug}/quiz`, { answers });
    if (data.passed) setProgress((p) => ({ ...p, [slug]: { ...p[slug], quiz_passed: true } }));
    if (data.certificate_id) await loadCertificates();
    return data;
  };

  return { user, progress, certificates, loaded, toggle, submitQuiz, reload };
}

export function courseStats(course, prog) {
  const ids = course.lessons.map((l) => l.id);
  const done = (prog?.completed_lessons || []).filter((id) => ids.includes(id));
  const total = course.lessons.length;
  const hasQuiz = (course.quiz_question_count || 0) > 0;
  const quizPassed = !!prog?.quiz_passed;
  const lessonsDone = total > 0 && done.length === total;
  const steps = total + (hasQuiz ? 1 : 0);
  const pct = steps ? Math.round(((done.length + (hasQuiz && quizPassed ? 1 : 0)) / steps) * 100) : 0;
  const next = course.lessons.find((l) => !done.includes(l.id)) || course.lessons[0];
  const nextPath = lessonsDone && hasQuiz && !quizPassed ? `/learn/${course.slug}/quiz` : `/learn/${course.slug}/${next.id}`;
  return { done, total, pct, next, nextPath, hasQuiz, quizPassed, lessonsDone, quizPending: lessonsDone && hasQuiz && !quizPassed,
    completed: lessonsDone && (!hasQuiz || quizPassed), started: done.length > 0 };
}

export const LEVEL_STYLES = {
  Beginner: "bg-mint/15 text-mint-600",
  Intermediate: "bg-gold/20 text-gold-600",
  Advanced: "bg-navy text-gold",
};

export const certUrl = (id) => `${window.location.origin}/certificates/${id}`;
export const certImage = (id, size = "full", v = "") => `${API}/certificates/${id}/image.png?size=${size}${v ? `&v=${encodeURIComponent(v)}` : ""}`;
export const certPdf = (id) => `${API}/certificates/${id}/certificate.pdf`;

export function linkedInAddUrl(cert) {
  const d = new Date(cert.issued_at);
  const p = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: `${cert.course_title} (${cert.course_level})`,
    organizationName: "Quantora",
    issueYear: String(d.getFullYear()),
    issueMonth: String(d.getMonth() + 1),
    certUrl: certUrl(cert.id),
    certId: cert.id,
  });
  return `https://www.linkedin.com/profile/add?${p.toString()}`;
}

export const linkedInShareUrl = (id) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certUrl(id))}`;
